import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import MoneyDisplay from 'components/util/MoneyDisplay';
import { useMemo } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { ClassifierData } from 'types/classifiers';
import { InvoiceData, InvoiceDetailsTotals, InvoiceTotalsForm } from 'types/invoices';
import { MatterBillingSummaryDto } from 'types/matters';
import { getClassifierText } from 'utils/getTranslations';
import { usePaymentCurrencyMode } from 'utils/usePaymentCurrencyMode';
import InvoiceTimecardTotalsTableItem from './InvoiceTimecardTotalsTableItem';
import InvoiceTotalsTableHeader from './InvoiceTotalsTableHeader';
import { TotalsTableCard } from './TotalsTableCard';

interface Props {
    invoiceTotals?: InvoiceDetailsTotals;
    invoice: InvoiceData;
    isInViewMode: boolean;
    index: number;
    classifiers: Record<string, ClassifierData>;
    matterId: number;
    paymentCurrency: string;
}

const DETAIL = 'text-fg-secondary flex gap-[0.5ch] text-[12px] leading-[1.375rem]';

const FeeArrangementDetails = ({
    matterBillingSummary,
    displayCurrency,
    isSplit,
    classifiers,
}: {
    matterBillingSummary?: MatterBillingSummaryDto;
    displayCurrency: string;
    isSplit: boolean;
    classifiers: Record<string, ClassifierData>;
}) => {
    const feeArrangementTypeCode = matterBillingSummary?.feeArrangementTypeCode || '';

    const capDisplayElements = [
        { key: 'cap-budget', value: matterBillingSummary?.budget?.capTotal },
        { key: 'cap-used', value: matterBillingSummary?.budget?.usedCap },
        { key: 'cap-remaining', value: matterBillingSummary?.budget?.remainingCap },
    ];

    return (
        <div className="-mt-1 flex flex-row flex-wrap gap-x-[13px] gap-y-1 px-4 pb-2">
            <p className={DETAIL}>
                <FormattedMessage
                    id="invoices.totals-tab.fee-arrangement"
                    values={{
                        value: (
                            <span key="value" className="text-fg font-semibold">
                                {isSplit ? (
                                    <FormattedMessage id="invoices.split-invoice" />
                                ) : (
                                    getClassifierText(classifiers['PRICING_LIMITATION_TYPE'], feeArrangementTypeCode)
                                )}
                            </span>
                        ),
                    }}
                />
            </p>
            {feeArrangementTypeCode === 'HOURLY_CAPPED'
                ? capDisplayElements.map((item) => (
                      <p key={item.key} className={DETAIL}>
                          <FormattedMessage
                              id={`invoices.totals-tab.${item.key}`}
                              values={{
                                  value: (
                                      <span key="value" className="text-fg font-semibold">
                                          <MoneyDisplay amount={item.value ?? 0} currency={displayCurrency} />
                                      </span>
                                  ),
                              }}
                          />
                      </p>
                  ))
                : null}
        </div>
    );
};

const InvoiceTimecardTotals = ({
    invoiceTotals,
    invoice,
    isInViewMode,
    index,
    classifiers,
    matterId,
    paymentCurrency,
}: Props) => {
    const { control } = useFormContext<InvoiceTotalsForm>();
    const { displayCurrency } = usePaymentCurrencyMode(invoice);
    const isNew = useIsNewDesign();
    const isSplit = invoice.isSplit ?? false;

    const { fields } = useFieldArray({
        control,
        name: `invoiceDetailsTotals.${index}.invoiceTimecardTotals`,
        keyName: 'formKey',
    });

    const tableContent = useMemo(
        () =>
            fields.map((field, index) => {
                return (
                    <InvoiceTimecardTotalsTableItem
                        key={field.formKey}
                        invoiceTotals={invoiceTotals?.invoiceTimecardTotals?.[index] || undefined}
                        invoice={invoice}
                        isInViewMode={isInViewMode}
                        matterId={matterId}
                    />
                );
            }),
        [fields, invoiceTotals?.invoiceTimecardTotals, invoice, isInViewMode, matterId]
    );

    return (
        <TotalsTableCard
            title={<FormattedMessage id="invoices.totals-tab.fees" />}
            isEmpty={!fields.length}
            subheader={
                // The new design's case card already says the case's arrangement and cap; Case totals names a split
                // only, beside the Fees title.
                isNew ? (
                    isSplit && (
                        <span className="text-meta text-foreground-tertiary">
                            <FormattedMessage
                                id="invoices.totals-tab.fee-arrangement"
                                values={{
                                    value: (
                                        <span key="value" className="text-foreground font-medium">
                                            <FormattedMessage id="invoices.split-invoice" />
                                        </span>
                                    ),
                                }}
                            />
                        </span>
                    )
                ) : (
                    <FeeArrangementDetails
                        matterBillingSummary={invoiceTotals?.matterBillingSummary}
                        displayCurrency={displayCurrency}
                        isSplit={isSplit}
                        classifiers={classifiers}
                    />
                )
            }
        >
            <InvoiceTotalsTableHeader paymentCurrency={paymentCurrency} />
            <div className="flex flex-col">{tableContent}</div>
        </TotalsTableCard>
    );
};

export default InvoiceTimecardTotals;
